import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader';
import PageTransition from '../../../components/common/PageTransition';
import Tooltip from '../../../components/common/Tooltip';
import { ReactionEditorModal } from '../../../components/chemistry/ReactionEditor';
import RouteDrawer from '../../../components/reactions/RouteDrawer';
import ConfidenceSignalCard from '../../../components/reactions/ConfidenceSignalCard';
import ExportButton from '../../../components/export/ExportButton';
import type { ReactionDetail, ReactionParticipant, V2ReactionDetail, V2Participant } from '../../../types/reactions';
import { StructureEditorModal } from '../../../components/chemistry/StructureEditor';

const roleBadge: Record<string, string> = {
  REACTANT: 'bg-blue-100 text-blue-800',
  PRODUCT: 'bg-emerald-100 text-emerald-800',
  REAGENT: 'bg-yellow-100 text-yellow-800',
  SOLVENT: 'bg-purple-100 text-purple-800',
  CATALYST: 'bg-orange-100 text-orange-800',
};

// ── SVG rendering via Indigo ──

const svgCache = new Map<string, string>();

const SmilesThumbnail: React.FC<{ smiles: string; width?: number; height?: number }> = ({
  smiles,
  width = 80,
  height = 60,
}) => {
  const [svg, setSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const cacheKey = `${smiles}|${width}x${height}`;
  const cached = svgCache.get(cacheKey);

  useEffect(() => {
    if (cached || !smiles) return;
    let cancelled = false;
    setLoading(true);
    const fetchSvg = async () => {
      try {
        const { default: apiClient } = await import('../../../api/client');
        const res = await apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: `${width}px`,
          height: `${height}px`,
        }, { responseType: 'text' });
        if (!cancelled) {
          svgCache.set(cacheKey, res.data);
          setSvg(res.data);
        }
      } catch {
        // silent — show fallback
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSvg();
    return () => { cancelled = true; };
  }, [smiles, width, height, cacheKey, cached]);

  const displaySvg = cached || svg;

  if (displaySvg) {
    return (
      <div
        className="bg-white rounded overflow-hidden"
        style={{ width, height }}
        dangerouslySetInnerHTML={{ __html: displaySvg }}
      />
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center bg-gray-50 rounded" style={{ width, height }}>
        <div className="w-3 h-3 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center bg-gray-100 rounded text-[10px] text-gray-400" style={{ width, height }}>
      {smiles ? smiles.substring(0, 8) : '—'}
    </div>
  );
};

const ReactionSvgViewer: React.FC<{ reactionSmiles: string; onClick?: () => void }> = ({ reactionSmiles, onClick }) => {
  const [svg, setSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!reactionSmiles) return;
    let cancelled = false;
    setLoading(true);
    setError(false);

    const fetchSvg = async () => {
      try {
        const { default: apiClient } = await import('../../../api/client');
        const res = await apiClient.post('/v2/indigo/render', {
          structure: { value: reactionSmiles, type: 'RAW' },
          width: '700px',
          height: '180px',
        }, { responseType: 'text' });
        if (!cancelled) setSvg(res.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSvg();
    return () => { cancelled = true; };
  }, [reactionSmiles]);

  if (loading) {
    return (
      <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !svg) {
    return (
      <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <code className="text-sm text-gray-500 max-w-3xl text-center break-all px-4">
          {reactionSmiles || 'No SMILES available'}
        </code>
      </div>
    );
  }

  return (
    <div
      className="group relative h-48 bg-white flex items-center justify-center overflow-hidden cursor-pointer hover:bg-blue-50 transition-colors [&>svg]:max-w-full [&>svg]:max-h-full"
      dangerouslySetInnerHTML={{ __html: svg }}
      onClick={onClick}
      title="Click to edit in Ketcher"
    />
  );
};

// ── Knowledge dot with instant tooltip ──

const KnowledgeDot: React.FC<{ present: boolean | null; label: string; fullLabel: string }> = ({
  present,
  label,
  fullLabel,
}) => {
  const statusText = present === true ? 'Present' : present === false ? 'Not found' : 'Unknown';
  return (
    <Tooltip content={`${fullLabel}: ${statusText}`}>
      <span
        className={`inline-block w-5 h-5 rounded-full text-[9px] font-bold leading-5 text-center cursor-default ${
          present === true ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
        }`}
      >
        {label}
      </span>
    </Tooltip>
  );
};

// ── Export helper ──

const downloadFile = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'chemical/x-mdl-rxnfile' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── Main page ──

const gradeColors: Record<string, string> = {
  HIGH: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
  MEDIUM: 'bg-yellow-50 text-yellow-700 border border-yellow-300',
  LOW: 'bg-red-50 text-red-700 border border-red-300',
};

/** Map a V2 participant to the shape the UI expects. */
function mapParticipant(p: V2Participant): ReactionParticipant {
  const enrichment = (p.enrichment && typeof p.enrichment === 'object') ? p.enrichment : {};
  const kc = (enrichment as any).knowledge_centre || {};
  return {
    id: p.id,
    role: (p.role || 'UNKNOWN') as ReactionParticipant['role'],
    component_no: p.component_no,
    structure: { smiles: p.smiles, inchi_key: p.inchi_key },
    user_molecule: {
      molecule_id: p.molecule_id,
      file_id: null,
      mol_index: null,
      match_confidence: p.match_confidence,
    },
    enrichment: {
      knowledge_centre: {
        surechembl: kc.surechembl || { present: null, ids: null },
        chembl: kc.chembl || { present: null, chembl_id: null },
        pubchem: kc.pubchem || { present: null, cid: null },
      },
      patents: (enrichment as any).patents || { zone: null, count: null },
      biology: (enrichment as any).biology || { targets: [], pathways: [] },
    },
  };
}

/** Map a V2ReactionDetail to the ReactionDetail shape the page renders. */
function mapV2ToDetail(v2: V2ReactionDetail): ReactionDetail {
  const participants = (v2.participants || []).map(mapParticipant);
  return {
    id: v2.id,
    projectId: v2.project_id,
    tenantId: v2.tenant_id,
    source: v2.source || 'MANUAL_UPLOAD',
    source_ref: v2.source_ref,
    file: {
      id: v2.file?.id || 0,
      name: v2.file?.name || 'Unknown',
      import_job_id: null,
      source_row: null,
    },
    reaction: {
      reaction_smiles: v2.reaction_smiles,
      atom_mapped_smiles: v2.atom_mapped_smiles,
      rxnfile: v2.rxnfile,
      notes: v2.notes,
    },
    conditions: v2.conditions || {
      solvents: null, reagents_text: null, catalysts: null,
      temperature_c: null, time_h: null, pressure_bar: null, ph: null, atmosphere: null,
    },
    outcome: v2.outcome || { yield_percent: null, yield_type: null },
    participants,
    enrichment_rollup: {
      matched_participants: participants.filter(p => p.user_molecule.molecule_id != null).length,
      total_participants: participants.length,
      patent_collisions_total: 0,
      targets_total: 0,
      pathways_total: 0,
    },
    reaction_type: v2.reaction_type || null,
    created_at: v2.created_at || new Date().toISOString(),
    updated_at: v2.updated_at || new Date().toISOString(),
  };
}

const ReactionDetailPage: React.FC = () => {
  const { reactionId } = useParams<{ reactionId: string }>();
  const [detail, setDetail] = useState<ReactionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [ketcherOpen, setKetcherOpen] = useState(false);
  const [routeContext, setRouteContext] = useState<
    { route_id: number; route_name: string; status: string; step_number: number; total_steps: number }[]
  >([]);
  const [openRouteId, setOpenRouteId] = useState<number | null>(null);
  const [confidence, setConfidence] = useState<any>(null);
  const [confidenceLoading, setConfidenceLoading] = useState(false);
  const [stringencyMode, setStringencyMode] = useState<'strict' | 'standard' | 'exploratory'>('standard');
  const [viewerSmiles, setViewerSmiles] = useState<string | null>(null);

  // Fetch reaction detail from v2 API
  useEffect(() => {
    if (!reactionId) return;
    let cancelled = false;
    setDetailLoading(true);
    const fetchDetail = async () => {
      try {
        const { default: apiClient } = await import('../../../api/client');
        const res = await apiClient.get(`/v2/reactions/${reactionId}`);
        if (!cancelled && res.data) {
          setDetail(mapV2ToDetail(res.data));
        }
      } catch {
        // silent — "not found" state handled below
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    fetchDetail();
    return () => { cancelled = true; };
  }, [reactionId]);

  useEffect(() => {
    if (!reactionId) return;
    let cancelled = false;
    const fetchRoutes = async () => {
      try {
        const { default: apiClient } = await import('../../../api/client');
        const res = await apiClient.get(`/v2/reactions/${reactionId}/routes`);
        if (!cancelled && res.data?.routes) {
          setRouteContext(res.data.routes);
        }
      } catch {
        // silent — no route context available
      }
    };
    fetchRoutes();
    return () => { cancelled = true; };
  }, [reactionId]);

  useEffect(() => {
    if (!reactionId) return;
    let cancelled = false;
    setConfidenceLoading(true);
    const fetchConfidence = async () => {
      try {
        const { default: apiClient } = await import('../../../api/client');
        const res = await apiClient.get(`/v2/reactions/${reactionId}/confidence`);
        if (!cancelled && res.data) {
          setConfidence(res.data);
        }
      } catch {
        // silent — pill just won't appear
      } finally {
        if (!cancelled) setConfidenceLoading(false);
      }
    };
    fetchConfidence();
    return () => { cancelled = true; };
  }, [reactionId]);

  const handleSaveReaction = useCallback(async (rxnfile: string, smiles: string) => {
    if (!reactionId) return;
    try {
      const { default: apiClient } = await import('../../../api/client');
      await apiClient.put(`/reactions/${reactionId}`, {
        rxnfile,
        reaction_smiles: smiles,
      });
      // Update local state to reflect the saved data
      setDetail(prev => prev ? {
        ...prev,
        reaction: { ...prev.reaction, rxnfile, reaction_smiles: smiles },
        updated_at: new Date().toISOString(),
      } : prev);
    } catch (err) {
      console.error('[ReactionDetail] Failed to save reaction:', err);
    }
  }, [reactionId]);

  const [isExporting, setIsExporting] = useState(false);

  const handleExportRxn = useCallback(async () => {
    if (!detail) return;
    const { reaction } = detail;
    setIsExporting(true);

    try {
      // If rxnfile is available, download it directly
      if (reaction.rxnfile) {
        downloadFile(reaction.rxnfile, `reaction_${detail.id}.rxn`);
        return;
      }

      // Otherwise generate from reaction SMILES via Indigo
      if (reaction.reaction_smiles) {
        try {
          const { default: apiClient } = await import('../../../api/client');
          const res = await apiClient.post('/v2/indigo/convert', {
            struct: reaction.reaction_smiles,
            output_format: 'chemical/x-mdl-rxnfile',
          });
          const rxnfile = typeof res.data === 'string' ? res.data : res.data.struct || res.data.structure;
          if (rxnfile && rxnfile.trim()) {
            downloadFile(rxnfile, `reaction_${detail.id}.rxn`);
            return;
          }
        } catch (err) {
          console.error('Export RXN conversion failed:', err);
        }
        // Fallback: download SMILES as text
        downloadFile(reaction.reaction_smiles, `reaction_${detail.id}.smiles`);
        return;
      }

      alert('No RXN data available for this reaction.');
    } finally {
      setIsExporting(false);
    }
  }, [detail]);

  if (detailLoading) {
    return (
      <PageTransition>
        <div className="flex flex-col items-center justify-center min-h-screen" style={{ background: 'var(--content-bg)' }}>
          <div className="w-8 h-8 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin mb-4" />
          <p className="text-sm text-gray-400">Loading reaction...</p>
        </div>
      </PageTransition>
    );
  }

  if (!detail) {
    return (
      <PageTransition>
        <div className="flex flex-col items-center justify-center min-h-screen" style={{ background: 'var(--content-bg)' }}>
          <p className="text-xl text-gray-500">Reaction not found</p>
          <Link to="/chemistry/reactions" className="mt-4 text-emerald-600 hover:underline">Back to reactions</Link>
        </div>
      </PageTransition>
    );
  }

  const { reaction, conditions: cond, outcome, participants, enrichment_rollup: er, file } = detail;

  return (
    <PageTransition>
      <div className="flex flex-col min-h-screen" style={{ background: 'var(--content-bg)' }}>
        <PageHeader
          title={`Reaction #${detail.id}`}
          subtitle={reaction.notes || undefined}
          backTo="/chemistry/reactions"
          backLabel="Reactions"
          breadcrumbs={[
            { label: 'Chemistry', to: '/chemistry/files' },
            { label: 'Reactions', to: '/chemistry/reactions' },
            { label: `#${detail.id}` },
          ]}
          actions={
            <div className="flex items-center gap-3">
              {detail.reaction_type && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {detail.reaction_type}
                </span>
              )}
              {confidenceLoading && (
                <span className="px-3 py-1 rounded-full text-xs font-mono bg-gray-100 text-gray-400 animate-pulse">
                  ···
                </span>
              )}
              {confidence && !confidenceLoading && (
                <span className={`px-3 py-1 rounded-full text-xs font-mono font-bold ${gradeColors[confidence.modes[stringencyMode].grade] || ''}`}>
                  {confidence.modes[stringencyMode].grade}
                </span>
              )}
              {confidence && (
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                  {(['strict', 'standard', 'exploratory'] as const).map(mode => (
                    <button
                      key={mode}
                      onClick={() => setStringencyMode(mode)}
                      className={`px-3 py-1 text-xs font-mono capitalize transition-colors ${
                        stringencyMode === mode
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              )}
              <ExportButton
                dataType="reactions"
                totalCount={1}
                reactionIds={[detail.id]}
                selectedCount={1}
              />
            </div>
          }
        />

        <div className="flex-1 px-6 pb-8 max-w-6xl mx-auto w-full space-y-6 pt-4">
          {/* ── Route context strip ── */}
          {routeContext.length === 0 && !detailLoading && (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-4 py-4 text-center">
              <p className="text-sm font-medium text-gray-500">No synthesis routes detected yet</p>
              <p className="text-xs text-gray-400 mt-1">Routes appear automatically when reactions share intermediate molecules</p>
            </div>
          )}
          {routeContext.length > 0 && routeContext.map((rc) => (
            rc.step_number != null && rc.total_steps != null && (
              <button
                key={rc.route_id}
                onClick={() => setOpenRouteId(rc.route_id)}
                className="w-full rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm text-teal-900 text-left hover:bg-teal-100 hover:border-teal-300 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-900">Route</span>
                  <span>
                    {rc.route_name || `Route #${rc.route_id}`} &middot; Step {rc.step_number} of {rc.total_steps}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-teal-600">
                    {rc.status && (
                      <span className="rounded bg-teal-100 px-2 py-0.5 text-teal-700">{rc.status}</span>
                    )}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </button>
            )
          ))}
          {/* ── Confidence summary + signals ── */}
          {confidence && (
            <ConfidenceSignalCard confidence={confidence} stringencyMode={stringencyMode} />
          )}

          {/* ── Reaction viewer + conditions ── */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <ReactionSvgViewer reactionSmiles={reaction.reaction_smiles || ''} onClick={() => setKetcherOpen(true)} />
            <div className="flex flex-wrap gap-4 px-6 py-4 border-t border-gray-100">
              {outcome.yield_percent != null && (
                <Pill label="Yield" value={`${outcome.yield_percent}%`} accent />
              )}
              {cond.temperature_c != null && <Pill label="Temp" value={`${cond.temperature_c} °C`} />}
              {cond.solvents?.length ? <Pill label="Solvent" value={cond.solvents.join(', ')} /> : null}
              {cond.time_h != null && <Pill label="Time" value={`${cond.time_h} h`} />}
              {cond.catalysts?.length ? <Pill label="Catalyst" value={cond.catalysts.join(', ')} /> : null}
              {cond.pressure_bar != null && <Pill label="Pressure" value={`${cond.pressure_bar} bar`} />}
              {cond.atmosphere && <Pill label="Atmosphere" value={cond.atmosphere} />}
            </div>
          </div>

          {/* ── 1. Participants ── */}
          <Section title="Participants">
            {participants.length === 0 ? (
              <p className="text-gray-400 text-sm py-4">No participants recorded</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                      <th className="py-3 px-3">Role</th>
                      <th className="py-3 px-3">Structure</th>
                      <th className="py-3 px-3">SMILES</th>
                      <th className="py-3 px-3">Linked molecule</th>
                      <th className="py-3 px-3">Confidence</th>
                      <th className="py-3 px-3">Knowledge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <ParticipantRow key={p.id} p={p} onStructureClick={setViewerSmiles} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* ── 2. Intelligence ── */}
          <Section title="Intelligence">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoCard
                title="Patent Collisions"
                value={er.patent_collisions_total}
                color={er.patent_collisions_total > 0 ? 'red' : 'gray'}
                detail={er.patent_collisions_total > 0 ? 'Review in detail view' : 'None detected'}
              />
              <InfoCard
                title="Knowledge Centre"
                value={`${er.matched_participants}/${er.total_participants}`}
                color="emerald"
                detail="Participants matched to known databases"
              />
              <InfoCard
                title="Biology"
                value={er.targets_total + er.pathways_total > 0 ? `${er.targets_total} targets, ${er.pathways_total} pathways` : '—'}
                color={er.targets_total > 0 ? 'blue' : 'gray'}
                detail={er.targets_total === 0 && er.pathways_total === 0 ? 'Coming soon' : undefined}
              />
            </div>
          </Section>

          {/* ── 3. Provenance ── */}
          <Section title="Provenance">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <ProvenanceItem label="Source" value={detail.source} />
              <ProvenanceItem label="Source ref" value={detail.source_ref || '—'} />
              <ProvenanceItem
                label="File"
                value={file.name}
                link={`/chemistry/file/${file.id}/editor`}
              />
              <ProvenanceItem label="Source row" value={file.source_row != null ? String(file.source_row) : '—'} />
              <ProvenanceItem
                label="Import job"
                value={file.import_job_id != null ? `#${file.import_job_id}` : '—'}
              />
              <ProvenanceItem label="Created" value={new Date(detail.created_at).toLocaleString()} />
              <ProvenanceItem label="Updated" value={new Date(detail.updated_at).toLocaleString()} />
            </div>
          </Section>
        </div>
      </div>

      <ReactionEditorModal
        isOpen={ketcherOpen}
        onClose={() => setKetcherOpen(false)}
        onSave={handleSaveReaction}
        initialReaction={reaction.rxnfile || reaction.reaction_smiles || ''}
        title={`Edit Reaction #${detail.id}`}
      />

      {viewerSmiles && (
        <StructureEditorModal
          isOpen={!!viewerSmiles}
          onClose={() => setViewerSmiles(null)}
          initialStructure={viewerSmiles}
          title="View Structure"
        />
      )}

      {openRouteId != null && (
        <RouteDrawer
          routeId={openRouteId}
          reactionId={detail.id}
          onClose={() => setOpenRouteId(null)}
        />
      )}
    </PageTransition>
  );
};

// ── Helpers ──

const Pill: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div className={`px-3 py-1.5 rounded-lg text-sm ${accent ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-100'}`}>
    <span className="text-gray-400 text-xs mr-1">{label}</span>
    <span className={`font-semibold ${accent ? 'text-emerald-700' : 'text-gray-800'}`}>{value}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{title}</h2>
    {children}
  </div>
);

const InfoCard: React.FC<{ title: string; value: string | number; color: string; detail?: string }> = ({
  title,
  value,
  color,
  detail,
}) => {
  const colorMap: Record<string, string> = {
    red: 'from-red-50 to-red-100 text-red-700',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700',
    blue: 'from-blue-50 to-blue-100 text-blue-700',
    gray: 'from-gray-50 to-gray-100 text-gray-500',
  };
  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.gray} rounded-xl p-4`}>
      <p className="text-xs font-bold uppercase tracking-wider mb-1 opacity-70">{title}</p>
      <p className="text-2xl font-bold">{value}</p>
      {detail && <p className="text-xs mt-1 opacity-60">{detail}</p>}
    </div>
  );
};

const ProvenanceItem: React.FC<{ label: string; value: string; link?: string }> = ({ label, value, link }) => (
  <div>
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    {link ? (
      <Link to={link} className="text-emerald-600 hover:underline font-medium">{value}</Link>
    ) : (
      <p className="text-gray-800 font-medium">{value}</p>
    )}
  </div>
);

const ParticipantRow: React.FC<{ p: ReactionParticipant; onStructureClick?: (smiles: string) => void }> = ({ p, onStructureClick }) => {
  const kc = p.enrichment.knowledge_centre;
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50">
      <td className="py-3 px-3">
        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${roleBadge[p.role] || 'bg-gray-100 text-gray-600'}`}>
          {p.role}
        </span>
      </td>
      <td className="py-3 px-3">
        <div
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => p.structure.smiles && onStructureClick?.(p.structure.smiles)}
          title="Click to enlarge"
        >
          <SmilesThumbnail smiles={p.structure.smiles || ''} width={80} height={60} />
        </div>
      </td>
      <td className="py-3 px-3 font-mono text-xs max-w-[200px] truncate" title={p.structure.smiles || ''}>
        {p.structure.smiles || '—'}
      </td>
      <td className="py-3 px-3">
        {p.user_molecule.molecule_id ? (
          <Link to={`/molecule/${p.user_molecule.molecule_id}`} className="text-emerald-600 hover:underline text-xs font-medium">
            Mol #{p.user_molecule.molecule_id}
          </Link>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
      </td>
      <td className="py-3 px-3 text-xs">
        {p.user_molecule.match_confidence != null ? (
          <span className="font-semibold text-emerald-700">{Math.round(p.user_molecule.match_confidence * 100)}%</span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="py-3 px-3">
        <div className="flex gap-1">
          <KnowledgeDot present={kc.surechembl.present} label="SC" fullLabel="SureChEMBL" />
          <KnowledgeDot present={kc.chembl.present} label="Ch" fullLabel="ChEMBL" />
          <KnowledgeDot present={kc.pubchem.present} label="PC" fullLabel="PubChem" />
        </div>
      </td>
    </tr>
  );
};

export default ReactionDetailPage;
